import React, { ReactNode, Component } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom';
import { useAuth, AuthProvider, isTokenExpired } from './auth-context';
import { isPlatformEmail } from './platform-email';

// ---------------------------------------------------------------------------
// ProtectedRoute
// ---------------------------------------------------------------------------

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<'admin' | 'analyst' | 'member_services'>;
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user } = useAuth();
  const location = useLocation();

  // If user state hasn't settled yet but a valid token is in localStorage,
  // show a blank screen for one tick rather than flashing /unauthorized or /login.
  if (!user) {
    const raw = localStorage.getItem('access_token');
    if (raw && !isTokenExpired(raw)) {
      return <div className="min-h-screen bg-gray-50" />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isTokenExpired(user.token)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform admins (cu-2.com / jasperdrake.com) bypass role checks — they have full access
  const isPlatform = isPlatformEmail(user.email);
  if (!isPlatform && allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

// Redirects already-authenticated users away from public pages (e.g. /login)
function PublicRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user && !isTokenExpired(user.token)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Nav sidebar (role-aware)
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  path: string;
  roles: Array<'admin' | 'analyst' | 'member_services'>;
}

interface NavGroup {
  groupLabel: string;
  roles: Array<'admin' | 'analyst' | 'member_services'>;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'groupLabel' in entry;
}

// Flattened (label, path, roles) for every real nav destination — exported
// so e2e/portal.spec.ts always sweeps whatever the sidebar actually
// contains, rather than a hand-maintained copy that silently stops covering
// a route the day someone adds or renames one here.
export function flattenNavItems(entries: NavEntry[]): NavItem[] {
  return entries.flatMap((entry) => (isNavGroup(entry) ? entry.items : [entry]));
}

export const NAV_ITEMS: NavEntry[] = [
  { label: 'Dashboard',          path: '/dashboard', roles: ['admin', 'analyst', 'member_services'] },
  { label: 'Performance Report', path: '/scorecard', roles: ['admin', 'analyst'] },
  {
    groupLabel: 'Members',
    roles: ['admin', 'analyst'],
    items: [
      { label: 'Member List',  path: '/members',        roles: ['admin', 'analyst'] },
      { label: 'Loans',        path: '/loans',          roles: ['admin', 'analyst'] },
      { label: 'Import Loans', path: '/members/upload', roles: ['admin'] },
    ],
  },
  {
    groupLabel: 'Campaigns',
    roles: ['admin', 'analyst'],
    items: [
      { label: 'Campaigns',         path: '/campaigns',          roles: ['admin', 'analyst'] },
      { label: 'Launcher',          path: '/campaigns/launcher', roles: ['admin'] },
      { label: 'SMS Registration',  path: '/sms-registration',   roles: ['admin'] },
    ],
  },
  {
    groupLabel: 'Compliance',
    roles: ['admin', 'analyst'],
    items: [
      { label: 'Audit Log',       path: '/audit-log',       roles: ['admin', 'analyst'] },
      { label: 'Consent',         path: '/consent',         roles: ['admin'] },
      { label: 'Offer Approvals', path: '/offer-approvals', roles: ['admin'] },
      { label: 'Fair Lending',    path: '/ecoa',            roles: ['admin'] },
    ],
  },
  {
    groupLabel: 'Call Queue',
    roles: ['admin', 'member_services'],
    items: [
      { label: 'Queue',              path: '/call-queue',                     roles: ['admin', 'member_services'] },
      { label: 'Script Performance', path: '/call-queue/script-performance',  roles: ['admin'] },
    ],
  },
  {
    groupLabel: 'Member Journey',
    roles: ['admin'],
    items: [
      { label: 'Analytics',  path: '/member-journey/analytics', roles: ['admin'] },
      { label: 'MC Catalog', path: '/member-journey/catalog',   roles: ['admin'] },
      { label: 'Rewards',    path: '/member-journey/rewards',   roles: ['admin'] },
    ],
  },
  { label: 'Outbox',             path: '/outbox',                    roles: ['admin', 'member_services'] },
  {
    groupLabel: 'Member App',
    roles: ['admin', 'analyst', 'member_services'],
    items: [
      { label: 'Overview',           path: '/member-app',      roles: ['admin', 'analyst', 'member_services'] },
      { label: 'Push Notifications', path: '/member-app/push', roles: ['admin', 'member_services'] },
      { label: 'Payments',           path: '/payments',        roles: ['admin', 'member_services'] },
    ],
  },
  {
    groupLabel: 'Admin',
    roles: ['admin'],
    items: [
      { label: 'Settings', path: '/settings', roles: ['admin'] },
      { label: 'Team',     path: '/team',     roles: ['admin'] },
    ],
  },
];

function Nav() {
  const { user } = useAuth();
  const location = useLocation();

  // Groups start open if any child is active, otherwise closed
  const initOpen = (groupLabel: string, items: NavItem[]) =>
    items.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'));

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    NAV_ITEMS.forEach(e => {
      if (isNavGroup(e)) init[e.groupLabel] = initOpen(e.groupLabel, e.items);
    });
    return init;
  });

  const toggleGroup = (label: string) =>
    setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));

  if (!user) return null;

  const visibleEntries = NAV_ITEMS.filter((entry) => entry.roles.includes(user.role));

  return (
    <aside className="flex flex-col w-56 min-h-screen bg-gray-900 text-white py-6 px-4 shrink-0">
      <div className="mb-8">
        <span className="text-xl font-bold tracking-tight text-white">OnRamp</span>
        <span className="block text-xs text-gray-400 mt-1">CU Portal</span>
      </div>

      <nav className="flex-1">
        <ul className="space-y-0.5">
          {visibleEntries.map((entry) => {
            if (isNavGroup(entry)) {
              const visibleSubItems = entry.items.filter((sub) => sub.roles.includes(user.role));
              if (visibleSubItems.length === 0) return null;
              const isOpen = openGroups[entry.groupLabel] ?? false;
              const hasActive = visibleSubItems.some(s => location.pathname === s.path || location.pathname.startsWith(s.path + '/'));
              return (
                <li key={entry.groupLabel} className="mt-2">
                  <button
                    onClick={() => toggleGroup(entry.groupLabel)}
                    className={[
                      'w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      hasActive
                        ? 'text-blue-400 hover:bg-gray-800'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200',
                    ].join(' ')}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider">{entry.groupLabel}</span>
                    <span className={['text-xs transition-transform', isOpen ? 'rotate-90' : ''].join(' ')}>▸</span>
                  </button>
                  {isOpen && (
                  <ul className="space-y-0.5 mt-0.5">
                    {visibleSubItems.map((sub) => {
                      const isActive = (() => {
                        // Exactly one item should highlight: whichever visible sub-item's
                        // path is the longest match for the current URL (exact match counts
                        // as the longest possible match against itself).
                        const matches = visibleSubItems.filter(
                          (s) => location.pathname === s.path || location.pathname.startsWith(s.path + '/'),
                        );
                        if (matches.length === 0) return false;
                        const best = matches.reduce((a, b) => (b.path.length > a.path.length ? b : a));
                        return best.path === sub.path;
                      })();
                      return (
                        <li key={sub.path}>
                          <Link
                            to={sub.path}
                            className={[
                              'block pl-5 pr-3 py-2 rounded-md text-sm font-medium transition-colors border-l-2 ml-3',
                              isActive
                                ? 'bg-blue-600 text-white border-blue-400'
                                : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200 border-gray-700',
                            ].join(' ')}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  )}
                </li>
              );
            }

            const item = entry as NavItem;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={[
                    'block px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                  ].join(' ')}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-8 border-t border-gray-700 pt-4">
        <p className="text-xs text-gray-400 mb-1 truncate">{user.email}</p>
        <p className="text-xs text-gray-500 capitalize">
          {user.role.replace('_', ' ')}
        </p>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Top bar (user email + sign-out, top-right)
// ---------------------------------------------------------------------------

function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  return (
    <div className="flex items-center justify-end h-10 px-4 bg-white border-b border-gray-200 shrink-0">
      {/* Always-visible entry point to the public walkthrough video -- lets
          staff pull it up for a stakeholder from anywhere in the portal,
          not just the Dashboard's Quick actions card. */}
      <a
        href="/demo"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 transition-colors mr-3"
      >
        ▶ Watch Demo
      </a>
      <span className="text-xs text-gray-500 mr-3 truncate max-w-[200px]">{user.email}</span>
      <button
        onClick={() => { logout(); navigate('/login'); }}
        className="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
      >
        Sign out
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page-level error boundary — sits INSIDE the layout so nav stays visible
// ---------------------------------------------------------------------------

const CHUNK_ERROR_MSGS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk',
  'ChunkLoadError',
];

class PageErrorBoundary extends Component<
  { children: ReactNode; routeKey: string },
  { error: Error | null; errorKey: string; reloading: boolean }
> {
  state = { error: null, errorKey: '', reloading: false };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error) {
    const msg = error.message + (error.name ?? '') + ((error as any).stack ?? '');
    if (CHUNK_ERROR_MSGS.some(p => msg.includes(p)) && !this.state.reloading) {
      this.setState({ reloading: true });
      setTimeout(() => window.location.reload(), 300);
    }
  }

  // Reset automatically when the route changes
  static getDerivedStateFromProps(
    props: { routeKey: string },
    state: { error: Error | null; errorKey: string; reloading: boolean },
  ) {
    // First-capture case must run before the route-change reset: this same method
    // runs again on the render that follows getDerivedStateFromError, while
    // errorKey is still '' -- checking the reset branch first matched
    // routeKey !== '' and cleared the error immediately, so the failing
    // children rendered again and threw a second time before the fallback UI
    // ever appeared.
    if (state.error && !state.errorKey) {
      return { errorKey: props.routeKey };
    }
    if (state.error && !state.reloading && props.routeKey !== state.errorKey) {
      return { error: null, errorKey: props.routeKey };
    }
    return null;
  }

  render() {
    if (this.state.reloading) {
      return (
        <div className="flex items-center justify-center h-full min-h-96">
          <p className="text-sm text-gray-400">Updating to latest version…</p>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-96 p-8">
          <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-md text-center shadow-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Something didn't load</h2>
            <p className="text-sm text-gray-500 mb-6">This usually fixes itself — try again or reload the page.</p>
            <div className="flex gap-3">
              <button
                onClick={() => this.setState({ error: null, errorKey: '', reloading: false })}
                className="flex-1 px-4 py-2.5 text-sm font-semibold border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Layout wrapper (sidebar + main content)
// ---------------------------------------------------------------------------

// Platform admins store a "platform token" in sessionStorage so they can
// switch back to the platform view after managing a specific CU.
const PLATFORM_TOKEN_KEY = 'platform_token';

function PlatformTopBar() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (!user) return null;

  const hasPlatformToken = !!sessionStorage.getItem(PLATFORM_TOKEN_KEY);

  function backToPlatform() {
    const token = sessionStorage.getItem(PLATFORM_TOKEN_KEY);
    if (token) { login(token); sessionStorage.removeItem(PLATFORM_TOKEN_KEY); navigate('/credit-unions', { replace: true }); }
  }

  return (
    <div className="flex items-center justify-between h-12 px-6 bg-gray-900 shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 rounded bg-blue-500 flex items-center justify-center text-white text-xs font-bold">C</div>
        <span className="text-sm font-semibold text-white">CU 2.0 Platform</span>
        <span className="text-xs text-gray-500 ml-2">Admin</span>
      </div>
      <div className="flex items-center gap-4">
        <Link to="/credit-unions" className={`text-xs font-medium transition-colors ${location.pathname === '/credit-unions' ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
          Credit Unions
        </Link>
        <Link to="/platform/campaign-review" className={`text-xs font-medium transition-colors ${location.pathname === '/platform/campaign-review' ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
          Campaign Review {/* badge injected by query below */}
        </Link>
        <span className="text-xs text-gray-500">|</span>
        <span className="text-xs text-gray-400">{user.email}</span>
        <button onClick={() => { logout(); navigate('/login'); }}
          className="text-xs text-gray-400 hover:text-white transition-colors">Sign out</button>
      </div>
    </div>
  );
}

const SWITCHED_CU_NAME_KEY = 'switched_cu_name';

function CUSwitchedBar() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const cuName = sessionStorage.getItem(SWITCHED_CU_NAME_KEY);

  function backToPlatform() {
    const token = sessionStorage.getItem(PLATFORM_TOKEN_KEY);
    sessionStorage.removeItem(SWITCHED_CU_NAME_KEY);
    if (token) { login(token); sessionStorage.removeItem(PLATFORM_TOKEN_KEY); navigate('/credit-unions', { replace: true }); }
    else { logout(); navigate('/login'); }
  }

  return (
    <div className="flex items-center justify-between h-9 px-4 bg-blue-700 shrink-0">
      <button onClick={backToPlatform} className="text-xs text-blue-100 hover:text-white font-medium">
        ← Back to Platform
      </button>
      <span className="text-xs text-blue-200">
        Managing <strong className="text-white">{cuName ?? 'a credit union'}</strong> — {user?.email}
      </span>
    </div>
  );
}

function AppLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const isPublicPage = ['/login', '/unauthorized', '/demo'].includes(location.pathname);
  const isEmailPlatformAdmin = isPlatformEmail(user?.email);
  // "switched" = platform admin who clicked Manage and is now inside a CU's portal
  const isSwitched = isEmailPlatformAdmin && !!sessionStorage.getItem(PLATFORM_TOKEN_KEY);
  // /credit-unions is always a platform-level route — never show the CU sidebar there
  const isPlatformRoute = location.pathname === '/credit-unions' || location.pathname.startsWith('/platform/');
  // Pure platform view = platform admin NOT currently managing a specific CU, OR on a platform route
  const isPlatformView = (isEmailPlatformAdmin && !isSwitched) || isPlatformRoute;

  if (isPlatformView && !isPublicPage) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <PlatformTopBar />
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {user && !isPublicPage && <Nav />}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {isSwitched && <CUSwitchedBar />}
        {user && !isPublicPage && <TopBar />}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lazy page imports
// ---------------------------------------------------------------------------

const LoginPage            = React.lazy(() => import('./pages/Login'));
const PublicDemoPage       = React.lazy(() => import('./pages/PublicDemo'));
const AuthCallbackPage     = React.lazy(() => import('./pages/AuthCallback'));
const DashboardPage        = React.lazy(() => import('./pages/Dashboard'));
const ScorecardPage        = React.lazy(() => import('./pages/Scorecard'));
const AuditLogPage         = React.lazy(() => import('./pages/AuditLog'));
const OfferApprovalsPage   = React.lazy(() => import('./pages/OfferApprovals'));
const EcoaPage             = React.lazy(() => import('./pages/Ecoa'));
const CallQueuePage        = React.lazy(() => import('./pages/CallQueue'));
const CallScriptPerformancePage = React.lazy(() => import('./pages/CallScriptPerformance'));
const MemberJourneyAnalyticsPage = React.lazy(() => import('./pages/MemberJourneyAnalytics'));
const MicroConversionCatalogPage = React.lazy(() => import('./pages/MicroConversionCatalog'));
const MemberRewardsPage          = React.lazy(() => import('./pages/MemberRewards'));
const SettingsPage           = React.lazy(() => import('./pages/Settings'));
const SmsRegistrationPage    = React.lazy(() => import('./pages/SmsRegistration'));
const MemberAppPage          = React.lazy(() => import('./pages/MemberApp'));
const PushNotificationsPage   = React.lazy(() => import('./pages/PushNotifications'));
const PaymentsPage            = React.lazy(() => import('./pages/Payments'));
const CampaignsPage              = React.lazy(() => import('./pages/Campaigns'));
const CampaignNewPage            = React.lazy(() => import('./pages/CampaignNew'));
const CampaignEditPage           = React.lazy(() => import('./pages/CampaignEdit'));
const CampaignAudiencePage       = React.lazy(() => import('./pages/CampaignAudience'));
const CampaignResultsPage        = React.lazy(() => import('./pages/CampaignResults'));
const CampaignLauncherPage       = React.lazy(() => import('./pages/CampaignLauncher'));
const CampaignLauncherPreviewPage= React.lazy(() => import('./pages/CampaignLauncherPreview'));
const CampaignLauncherLogPage    = React.lazy(() => import('./pages/CampaignLauncherLog'));
const CampaignLauncherRulesPage  = React.lazy(() => import('./pages/CampaignLauncherRules'));
const CampaignApprovePage        = React.lazy(() => import('./pages/CampaignApprove'));
const ConsentLookupPage          = React.lazy(() => import('./pages/ConsentLookup'));
const CreditUnionsPage           = React.lazy(() => import('./pages/CreditUnions'));
const TeamPage                   = React.lazy(() => import('./pages/Team'));
const InviteAcceptPage           = React.lazy(() => import('./pages/InviteAccept'));
const MembersPage                = React.lazy(() => import('./pages/Members'));
const LoansPage                  = React.lazy(() => import('./pages/Loans'));
const OutboxPage                 = React.lazy(() => import('./pages/Outbox'));
const PlatformCampaignReviewPage = React.lazy(() => import('./pages/PlatformCampaignReview'));
const MemberUploadPage           = React.lazy(() => import('./pages/MemberUpload'));

// ---------------------------------------------------------------------------
// Unauthorized page (inline — no lazy needed)
// ---------------------------------------------------------------------------

function UnauthorizedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // If the user IS authenticated, redirect them to the right dashboard immediately.
  // This handles race conditions where /unauthorized is hit during auth state settling.
  React.useEffect(() => {
    if (!user) return;
    const isPlatform = isPlatformEmail(user.email);
    navigate(isPlatform ? '/credit-unions' : '/dashboard', { replace: true });
  }, [user, navigate]);

  if (user) {
    // Redirecting — show nothing to avoid flash
    return <div className="min-h-screen bg-gray-50" />;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-lg shadow-md p-10 max-w-md text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Access Denied</h1>
        <p className="text-gray-500 mb-6">
          You do not have permission to view this page.
        </p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          Sign in
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root application
// ---------------------------------------------------------------------------

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const isPlatformAdmin = isPlatformEmail(user.email);
  return <Navigate to={isPlatformAdmin ? '/credit-unions' : '/dashboard'} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const [isPending, startTransition] = React.useTransition();
  void isPending;
  return (
    <AppLayout>
      <PageErrorBoundary routeKey={location.pathname}>
      <React.Suspense fallback={null}>
      {/* null fallback: React keeps the old page visible during lazy load */}
        <Routes>
          {/* Public — redirect away if already authenticated */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          {/* Entra redirect target (msal.ts redirectUri). Deliberately not wrapped
              in PublicRoute — MSAL must be allowed to process the auth response
              before any authenticated-user redirect can fire. */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          {/* Public walkthrough video -- no auth, reachable whether signed in or not */}
          <Route path="/demo" element={<PublicDemoPage />} />

          {/* Dashboard — all authenticated roles */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

          {/* Members — Admin + Analyst only (member_services excluded per privacy rules) */}
          <Route path="/members" element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><MembersPage /></ProtectedRoute>} />
          <Route path="/loans" element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><LoansPage /></ProtectedRoute>} />
          <Route path="/members/upload" element={<ProtectedRoute allowedRoles={['admin']}><MemberUploadPage /></ProtectedRoute>} />

          {/* Admin + Analyst */}
          <Route path="/scorecard" element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><ScorecardPage /></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><AuditLogPage /></ProtectedRoute>} />

          {/* Admin only */}
          <Route path="/consent"         element={<ProtectedRoute allowedRoles={['admin']}><ConsentLookupPage /></ProtectedRoute>} />
          <Route path="/offer-approvals" element={<ProtectedRoute allowedRoles={['admin']}><OfferApprovalsPage /></ProtectedRoute>} />
          <Route path="/ecoa"            element={<ProtectedRoute allowedRoles={['admin']}><EcoaPage /></ProtectedRoute>} />
          <Route path="/settings"          element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
          {/* Legacy redirects — old URLs forward to unified Settings */}
          <Route path="/config"                element={<Navigate to="/settings" replace />} />
          <Route path="/member-app/settings"   element={<Navigate to="/settings" replace />} />
          <Route path="/campaigns/channels"    element={<Navigate to="/settings" replace />} />
          <Route path="/sms-registration" element={<ProtectedRoute allowedRoles={['admin']}><SmsRegistrationPage /></ProtectedRoute>} />
          <Route path="/credit-unions"    element={<ProtectedRoute allowedRoles={['admin']}><CreditUnionsPage /></ProtectedRoute>} />
          <Route path="/platform/campaign-review" element={<ProtectedRoute allowedRoles={['admin']}><PlatformCampaignReviewPage /></ProtectedRoute>} />
          <Route path="/team"             element={<ProtectedRoute allowedRoles={['admin']}><TeamPage /></ProtectedRoute>} />
          <Route path="/member-app"          element={<ProtectedRoute allowedRoles={['admin','analyst','member_services']}><MemberAppPage /></ProtectedRoute>} />
          <Route path="/member-app/push"     element={<ProtectedRoute allowedRoles={['admin','member_services']}><PushNotificationsPage /></ProtectedRoute>} />
          <Route path="/payments"            element={<ProtectedRoute allowedRoles={['admin','member_services']}><PaymentsPage /></ProtectedRoute>} />

          {/* Member-Services + Admin */}
          <Route path="/call-queue" element={<ProtectedRoute allowedRoles={['admin', 'member_services']}><CallQueuePage /></ProtectedRoute>} />
          <Route path="/call-queue/script-performance" element={<ProtectedRoute allowedRoles={['admin']}><CallScriptPerformancePage /></ProtectedRoute>} />
          <Route path="/member-journey/analytics" element={<ProtectedRoute allowedRoles={['admin']}><MemberJourneyAnalyticsPage /></ProtectedRoute>} />
          <Route path="/member-journey/catalog" element={<ProtectedRoute allowedRoles={['admin']}><MicroConversionCatalogPage /></ProtectedRoute>} />
          <Route path="/member-journey/rewards" element={<ProtectedRoute allowedRoles={['admin']}><MemberRewardsPage /></ProtectedRoute>} />
          <Route path="/outbox"     element={<ProtectedRoute allowedRoles={['admin', 'member_services']}><OutboxPage /></ProtectedRoute>} />

          {/* Campaigns — Admin + Analyst */}
          <Route path="/campaigns"              element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><CampaignsPage /></ProtectedRoute>} />
          <Route path="/campaigns/new"          element={<ProtectedRoute allowedRoles={['admin']}><CampaignNewPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id/edit"     element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><CampaignEditPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id/audience" element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><CampaignAudiencePage /></ProtectedRoute>} />
          <Route path="/campaigns/:id/results"  element={<ProtectedRoute allowedRoles={['admin', 'analyst']}><CampaignResultsPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id/approve" element={<ProtectedRoute allowedRoles={['admin']}><CampaignApprovePage /></ProtectedRoute>} />

          {/* Campaign Launcher — Admin only (static routes before dynamic) */}
          <Route path="/campaigns/launcher"         element={<ProtectedRoute allowedRoles={['admin']}><CampaignLauncherPage /></ProtectedRoute>} />
          <Route path="/campaigns/launcher/preview" element={<ProtectedRoute allowedRoles={['admin']}><CampaignLauncherPreviewPage /></ProtectedRoute>} />
          <Route path="/campaigns/launcher/log"     element={<ProtectedRoute allowedRoles={['admin']}><CampaignLauncherLogPage /></ProtectedRoute>} />
          <Route path="/campaigns/launcher/:id/rules" element={<ProtectedRoute allowedRoles={['admin']}><CampaignLauncherRulesPage /></ProtectedRoute>} />

          {/* Default */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
      </PageErrorBoundary>
    </AppLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
